import AxiosInstance from '@/api/axios';
import {
  ConversationType,
  GeneralFilterType,
  PackageAccessType,
  VisitorDetailsType,
} from '@/utils/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface ChatFilterType extends GeneralFilterType {
  search?: string;
  offset?: number;
  limit?: number;
  page?: number;
  sortOrder?: string;
  users_reengaged?: any[];
  messages_sent?: any[];
  tour_booked?: any[];
  is_reengaged?: boolean;
  senders?: string[];
}
interface DownloadHistory extends GeneralFilterType {
  search?: string | null;
  sortOrder?: string | 'desc';
  users_reengaged?: any[];
  messages_sent?: any[];
  tour_booked?: any[];
  is_reengaged?: boolean;
}

interface DownloadReport extends GeneralFilterType {
  selectedMetric?: string | null;
}

function downloadFile(url: string, filename?: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

let abortController: AbortController | null = null;

export const fetchVisitorHistory = createAsyncThunk(
  'getChatHistory/fetchData',
  async (
    {
      organization_id,
      branch_id,
      region_id,
      agent,
      date_range,
      month_date_range,
      search,
      page,
      sortOrder,
      users_reengaged,
      messages_sent,
      tour_booked,
      is_reengaged,
      senders,
    }: ChatFilterType,
    thunkAPI
  ) => {
    if (branch_id === 'all') return;
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    const useList = Array.isArray(senders) && senders.length > 0;
    try {
      const response = await AxiosInstance.post(
        useList ? `/visitors/senders/by-list` : `/visitors/senders`,
        useList
          ? { senders }
          : { messages_sent, users_reengaged, tour_booked },
        {
          params: {
            organization_id,
            branch_id: branch_id,
            region_id: region_id,
            agents: agent,
            date_range: month_date_range,
            search,
            page,
            sortOrder,
            // is_reengaged only applies to the normal senders endpoint
            ...(useList ? {} : { is_reengaged }),
            perpage: 10,
          },
          signal: abortController.signal,
        }
      );

      if (response.data.success) {
        return {
          conversations: response.data?.data?.visitors,
          page: response.data?.data?.page,
          totalPages: response.data?.data?.totalPages,
          timezone: response.data?.data?.timezone,
        };
      } else {
        return null;
      }
    } catch (error: any) {
      return null;
    } finally {
      abortController = null;
    }
  }
);

export const downloadVisitorHistory = createAsyncThunk(
  'getChatHistory/downloadData',
  async (
    {
      organization_id,
      branch_id,
      region_id,
      agent,
      date_range,
      month_date_range,
      search,
      sortOrder,
      users_reengaged,
      messages_sent,
      tour_booked,
      is_reengaged,
    }: DownloadHistory,
    thunkAPI
  ) => {
    if (branch_id === 'all') return;
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    try {
      const response = await AxiosInstance.post(
        `/history`,
        {
          organization_id,
          branch_id: branch_id,
          region_id,
          agents: agent,
          date_range: month_date_range,
          search,
          sortOrder,
          users_reengaged,
          messages_sent,
          tour_booked,
          is_reengaged,
        },
        {
          signal: abortController.signal,
        }
      );

      if (response.data.success) {
        return {
          url: response?.data?.url,
        };
      } else {
        return {
          url: null,
        };
      }
    } catch (error: any) {
      return null;
    } finally {
      abortController = null;
    }
  }
);

export const downloadReportSlice = createAsyncThunk(
  'getReport/downloadReport',
  async (
    {
      organization_id,
      branch_id,
      region_id,
      agentMultiple,
      assetMultiple,
      month_date_range,
      selectedMetric,
      reengaged,
    }: DownloadReport,
    thunkAPI
  ) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    try {
      const response = await AxiosInstance.get(`/agents/download-report`, {
        params: {
          organization_id,
          branch_id: branch_id,
          region_id,
          agent: agentMultiple,
          assets: assetMultiple,
          date_range: month_date_range,
          selectedMetric,
          reengaged,
        },
        signal: abortController.signal,
        responseType: 'blob',
      });

      const blob = response?.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;

      // Get filename from backend header
      // const disposition = response.headers['Content-Disposition'];
      const disposition = response.headers['content-disposition'];
      let filename = 'report.xlsx';

      if (disposition) {
        const match = disposition.match(/filename="(.+)"/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return { url: null };
      // if (response.data.success) {
      //   return {
      //     url: response?.data?.url,
      //   };
      // } else {
      //   return {
      //     url: null,
      //   };
      // }
    } catch (error: any) {
      return null;
    } finally {
      abortController = null;
    }
  }
);
export const fetchChannel = createAsyncThunk(
  'getPackageChannel/channelPackage',
  async (clientId: string) => {
    try {
      const response = await AxiosInstance.get(
        `/billing/package/access/${clientId}`
      );

      return response;
    } catch (error: any) {
      return null;
    } finally {
      abortController = null;
    }
  }
);

interface VisitorsHistoryStateType {
  data: ConversationType | null | undefined;
  packageAccess: PackageAccessType | null | undefined;
  loading: boolean;
  chatLoading: boolean;
  exportDisabled: boolean;
  error: string | null;
  url: string | null;
}

const initialState: VisitorsHistoryStateType = {
  data: null,
  packageAccess: null,
  loading: false,
  chatLoading: false,
  exportDisabled: false,
  error: null,
  url: null,
};

const visitorsHistoryReducer = createSlice({
  name: 'visitorsHistory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVisitorHistory.pending, (state) => {
        state.chatLoading = true;
        state.error = null;
      })
      .addCase(fetchVisitorHistory.fulfilled, (state, action) => {
        state.chatLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchVisitorHistory.rejected, (state, action) => {
        state.chatLoading = false;
        state.error = action.payload as string;
      })
      .addCase(downloadVisitorHistory.pending, (state) => {
        state.loading = false;
        state.exportDisabled = true;
        state.error = null;
      })
      .addCase(downloadVisitorHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.exportDisabled = false;
        state.url = action.payload?.url ?? null;

        if (action.payload?.url) {
          downloadFile(action.payload?.url);
        }
      })
      .addCase(downloadVisitorHistory.rejected, (state, action) => {
        state.loading = false;
        state.exportDisabled = false;
        state.error = action.payload as string;
      })
      .addCase(downloadReportSlice.pending, (state) => {
        state.loading = false;
        state.exportDisabled = true;
        state.error = null;
      })
      .addCase(downloadReportSlice.fulfilled, (state, action) => {
        state.loading = false;
        state.exportDisabled = false;
        state.url = action.payload?.url ?? null;

        if (action.payload?.url) {
          downloadFile(action.payload?.url);
        }
      })
      .addCase(downloadReportSlice.rejected, (state, action) => {
        state.loading = false;
        state.exportDisabled = false;
        state.error = action.payload as string;
      })
      .addCase(fetchChannel.pending, (state) => {
        state.loading = false;
        state.packageAccess = null;
        state.error = null;
      })
      .addCase(fetchChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.packageAccess = action.payload?.data?.data ?? null;
        state.error = null;
      })
      .addCase(fetchChannel.rejected, (state, action) => {
        state.loading = false;
        state.packageAccess = null;
        state.error = action.payload as string;
      });
  },
});

export default visitorsHistoryReducer.reducer;
